import { useCallback, useEffect, useState } from "react";
import { getMyPermissions } from "../services/adminPermissionsService";

// Module-level cache — same pattern as useIsSuperAdmin.
// A single GET /admin/permissions/mine is made per session; all subscribers
// share the cached result.
interface CachedData {
  permissionKeys: string[];
  isSuperAdmin: boolean;
}

const EMPTY: CachedData = { permissionKeys: [], isSuperAdmin: false };

let cachedData: CachedData | null = null;
let inflight: Promise<CachedData> | null = null;
const listeners = new Set<(data: CachedData) => void>();

const fetchOnce = (): Promise<CachedData> => {
  if (cachedData !== null) return Promise.resolve(cachedData);
  if (inflight) return inflight;
  inflight = getMyPermissions()
    .then((res: any) => {
      const data: CachedData = {
        permissionKeys: res?.data?.permissionKeys ?? [],
        isSuperAdmin: !!(res?.isSuccessful && res?.data?.isSuperAdmin),
      };
      cachedData = data;
      listeners.forEach((cb) => cb(data));
      return data;
    })
    .catch(() => {
      const data = EMPTY;
      cachedData = data;
      listeners.forEach((cb) => cb(data));
      return data;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

// Call this on logout or session change. (Logout does a full page reload so
// this is exported for completeness but is typically not needed.)
export const resetAdminPermissionsCache = () => {
  cachedData = null;
  inflight = null;
};

export interface UseAdminPermissionsResult {
  hasPermission: (key: string) => boolean;
  permissionKeys: string[];
  isSuperAdmin: boolean;
  isLoading: boolean;
}

export const useAdminPermissions = (): UseAdminPermissionsResult => {
  const [data, setData] = useState<CachedData>(cachedData ?? EMPTY);
  const [isLoading, setIsLoading] = useState<boolean>(cachedData === null);

  useEffect(() => {
    let cancelled = false;

    const listener = (d: CachedData) => {
      if (!cancelled) {
        setData(d);
        setIsLoading(false);
      }
    };
    listeners.add(listener);

    if (cachedData !== null) {
      setData(cachedData);
      setIsLoading(false);
    } else {
      fetchOnce();
    }

    return () => {
      cancelled = true;
      listeners.delete(listener);
    };
  }, []);

  const hasPermission = useCallback(
    (key: string): boolean => {
      if (data.isSuperAdmin) return true;
      return data.permissionKeys.includes(key);
    },
    [data],
  );

  return {
    hasPermission,
    permissionKeys: data.permissionKeys,
    isSuperAdmin: data.isSuperAdmin,
    isLoading,
  };
};
